import React, { useEffect, useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import dayjs from 'dayjs'
import {
  CalendarIcon,
  CircleCheckIcon,
  Loader2,
  MinusIcon,
  PlusIcon,
} from 'lucide-react'
import { ControllerRenderProps } from 'react-hook-form'
import { toast } from 'sonner'

import { cn } from '@/lib/utils.ts'
import { useSmartForm } from '@/hooks/use-smart-form.tsx'
import { Button } from '@/components/ui/button.tsx'
import { Calendar } from '@/components/ui/calendar.tsx'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form.tsx'
import { Input } from '@/components/ui/input.tsx'
import { Label } from '@/components/ui/label'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover.tsx'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group.tsx'
import { AdaptiveTextarea } from '@/components/adaptive-textarea.tsx'
import { Combobox, ComboboxItem } from '@/components/combobox.tsx'
import { FormActions } from '@/components/form-actions.tsx'
import { PropertySchema } from '@/components/json-schema-generator/types'
import { getDocumentQueryOptions } from '@/features/documents/hooks/use-get-document.ts'
import { useUpdateDocumentById } from '@/features/documents/hooks/use-update-document.ts'
import { Document } from '@/features/documents/types.ts'

export interface DocumentFormProps {
  document?: Document
}

type DocumentFormData = {
  document?: Pick<Document, 'name' | 'summary'>
  attributes?: Record<string, unknown>
}

const NestedObjectFormControl: React.FC<{
  schema: PropertySchema
  field: Pick<
    ControllerRenderProps<DocumentFormData, `attributes.${string}`>,
    'value' | 'onChange'
  >
  path?: string
}> = ({ schema, field, path = '' }) => {
  const currentValue = (field.value as Record<string, unknown>) || {}

  const handleNestedChange = (propertyName: string, value: unknown) => {
    field.onChange({
      ...currentValue,
      [propertyName]: value,
    })
  }

  return (
    <div className="space-y-4 rounded-lg border p-4">
      {Object.entries(schema.properties || {}).map(
        ([propertyName, propertySchema]) => {
          const fullPath = path ? `${path}.${propertyName}` : propertyName

          return (
            <div key={propertyName} className="space-y-2">
              <Label className="text-sm font-medium">
                {propertySchema.title || propertyName}
              </Label>
              <AttributeTypeControl
                schema={propertySchema as PropertySchema}
                value={currentValue[propertyName]}
                onChange={(value) => handleNestedChange(propertyName, value)}
                path={fullPath}
              />
            </div>
          )
        }
      )}
    </div>
  )
}

const AttributeTypeControl: React.FC<{
  schema: PropertySchema
  value: unknown
  onChange: (value: unknown) => void
  path: string
}> = ({ schema, value, onChange, path }) => {
  const enumOptions = useMemo(
    () =>
      schema.enum?.map((v) => ({
        label: v,
        value: v,
      })) as Array<ComboboxItem>,
    [schema.enum]
  )

  if (schema.enum) {
    return (
      <Combobox
        className="w-full"
        items={enumOptions}
        value={value as string}
        onValueChange={onChange}
        allowClear
      />
    )

    // return (
    //   <Select value={value?.toString()} onValueChange={onChange}>
    //     <SelectTrigger>
    //       <SelectValue />
    //     </SelectTrigger>
    //     <SelectContent>
    //       {schema.enum.map((val) => (
    //         <SelectItem key={val?.toString()} value={val?.toString() || ''}>
    //           {val?.toString()}
    //         </SelectItem>
    //       ))}
    //     </SelectContent>
    //   </Select>
    // )
  }

  if (schema.type === 'object') {
    return (
      <NestedObjectFormControl
        schema={schema}
        field={{ value, onChange }}
        path={path}
      />
    )
  }

  if (schema.type === 'array') {
    const values = Array.isArray(value) ? value : value ? [value] : []

    const handleAddItem = () => {
      const newItem = schema.items?.type === 'object' ? {} : ''
      onChange([...values, newItem])
    }

    const handleRemoveItem = (index: number) => {
      onChange([...values.slice(0, index), ...values.slice(index + 1)])
    }

    return (
      <div className="space-y-2">
        {values.map((val, index) => (
          <div key={index} className="flex gap-2">
            <div className="flex-1">
              <AttributeTypeControl
                schema={schema.items as PropertySchema}
                value={val}
                onChange={(newVal) => {
                  const newValues = [...values]
                  newValues[index] = newVal
                  onChange(newValues)
                }}
                path={`${path}.${index}`}
              />
            </div>
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => handleRemoveItem(index)}
            >
              <MinusIcon className="size-4" />
            </Button>
          </div>
        ))}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleAddItem}
        >
          <PlusIcon className="mr-2 size-4" /> Aggiungi
        </Button>
      </div>
    )
  }

  if (schema.type === 'boolean') {
    return (
      <RadioGroup
        value={String(!!value)}
        onValueChange={(v) => onChange(v === 'true')}
        className="flex items-center space-x-2"
      >
        <Label className="flex cursor-pointer items-center space-x-2">
          <RadioGroupItem value="true" />
          <span>Si</span>
        </Label>

        <Label className="flex cursor-pointer items-center space-x-2">
          <RadioGroupItem value="false" />
          <span>No</span>
        </Label>
      </RadioGroup>
    )
    return <Checkbox checked={!!value} onCheckedChange={onChange} />
  }

  if (schema.format === 'date') {
    return (
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              'w-full pl-3 text-left font-normal',
              !value && 'text-muted-foreground'
            )}
          >
            {value ? (
              dayjs(value.toString()).format('DD/MM/YYYY')
            ) : (
              <span>Pick a date</span>
            )}
            <CalendarIcon className="ml-auto size-4 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={value ? dayjs(value.toString()).toDate() : undefined}
            onSelect={(value) =>
              onChange(
                value
                  ? dayjs(value.toString())
                      .tz('Europe/Rome')
                      .format('YYYY-MM-DD')
                  : undefined
              )
            }
            initialFocus
          />
        </PopoverContent>
      </Popover>
    )
  }

  // Default to text input for strings and numbers
  return (
    <Input
      type={schema.type === 'number' ? 'number' : 'text'}
      value={value?.toString() || ''}
      onChange={(e) => {
        const val =
          schema.type === 'number' ? parseFloat(e.target.value) : e.target.value
        onChange(val)
      }}
    />
  )
}

const AttributeFormControl: React.FC<{
  documentAttribute: Document['attributes'][number]
  field: ControllerRenderProps<DocumentFormData, `attributes.${string}`>
}> = ({ documentAttribute, field }) => {
  const schema = documentAttribute.attribute.json_schema

  if (!schema) {
    return null
  }

  return (
    <AttributeTypeControl
      schema={schema as PropertySchema}
      value={field.value}
      onChange={field.onChange}
      path={documentAttribute.attribute.id}
    />
  )
}

export const DocumentForm: React.FC<DocumentFormProps> = ({ document }) => {
  const navigate = useNavigate()
  const defaultValues = useMemo(() => {
    if (!document) return {}

    return {
      document,
      attributes: document?.attributes.reduce(
        (acc, documentAttribute) => ({
          ...acc,
          [documentAttribute.attribute.id]: documentAttribute.value,
        }),
        {}
      ),
    }
  }, [document])

  const queryClient = useQueryClient()
  const updateDocumentById = useUpdateDocumentById({
    onSuccess: () => {
      toast('Documento salvato', { icon: <CircleCheckIcon /> })
      navigate({
        from: '/documents/$documentId',
        search: (s) => ({ ...s, mode: 'view' }),
      })

      return queryClient.invalidateQueries({
        queryKey: getDocumentQueryOptions({ documentId: document!.id })
          .queryKey,
      })
    },
  })
  const form = useSmartForm<DocumentFormData>({
    defaultValues,
  })
  useEffect(() => {
    form.reset(defaultValues)
  }, [defaultValues, form])

  const onSubmit = (data: DocumentFormData) => {
    return updateDocumentById.mutateAsync({
      ...data,
      documentId: document!.id!,
    })
  }

  return (
    <>
      {/*{['processing', 'in_review'].includes(document.status) && (*/}
      {/*  <Alert className="mb-4 border-yellow-500" variant="default">*/}
      {/*    <AlertCircle className="size-4 stroke-yellow-500" />*/}
      {/*    <AlertTitle className="text-yellow-500">*/}
      {/*      {document?.status === 'processing' ? (*/}
      {/*        <>Il documento è ancora in fase di analisi</>*/}
      {/*      ) : (*/}
      {/*        <>Il documento necessita di revisione</>*/}
      {/*      )}*/}
      {/*    </AlertTitle>*/}
      {/*  </Alert>*/}
      {/*)}*/}

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <FormField
            control={form.control}
            name="document.name"
            render={({ field }) => (
              <FormItem className="mb-6">
                <FormLabel>Nome</FormLabel>
                <FormControl>
                  <AdaptiveTextarea className="min-h-0" rows={1} {...field} />
                </FormControl>
                <FormDescription></FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="document.summary"
            render={({ field }) => (
              <FormItem className="mb-6">
                <FormLabel>Descrizione</FormLabel>
                <FormControl>
                  <AdaptiveTextarea
                    className="min-h-0"
                    rows={1}
                    {...field}
                    value={field.value?.toString()}
                  />
                </FormControl>
                <FormDescription></FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <h3 className="mb-4 text-lg font-medium">Attributi</h3>

          {document?.attributes.map((documentAttribute) => (
            <FormField
              key={documentAttribute.attribute.id}
              control={form.control}
              name={`attributes.${documentAttribute.attribute.id}`}
              render={({ field }) => (
                <FormItem className="mb-6">
                  <FormLabel>{documentAttribute.attribute.name}</FormLabel>
                  <FormControl>
                    <AttributeFormControl
                      field={field}
                      documentAttribute={documentAttribute}
                    />
                    {/*<Input {...field} value={field.value?.toString()} />*/}
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          ))}
          <FormActions>
            <Button
              type="reset"
              variant="outline"
              size="lg"
              onClick={() => {
                form.reset()
                navigate({
                  to: '/documents/$documentId',
                  params: { documentId: document!.id },
                  search: (s) => ({ ...s, mode: 'view' }),
                })
              }}
            >
              Annulla
            </Button>
            <Button
              type="submit"
              size="lg"
              disabled={form.formState.isSubmitting || !form.formState.isDirty}
            >
              {form.formState.isSubmitting && (
                <Loader2 className="mr-2 size-4 animate-spin" />
              )}
              Salva
            </Button>
          </FormActions>
        </form>
      </Form>
    </>
  )
}
